// ===================================================================
// src/app/services/chatService.ts
//
// Cliente REST para o chat interno da equipe (apps.chat no eleveia-api).
// Usa o wrapper `api` de src/app/lib/api.ts — mesma convenção de
// nicoService.ts (JWT auto-refresh, ApiError em falhas).
//
// A camada em tempo real (WebSocket) vive em src/app/lib/chatSocket.ts —
// este arquivo cobre apenas REST (histórico, CRUD, reações, anexos).
// ===================================================================

import { api, getAccessToken } from '@/app/lib/api';
import { ENV } from '@/app/lib/env';

// ── Tipos ────────────────────────────────────────────────────────────────────

/** UserMiniSerializer — apps/boards/serializers/user_mini/user_mini_serializer.py */
export interface ChatUserMini {
  id: number;
  username: string;
  full_name: string;
  /** URL absoluta da foto de perfil (UserProfile.avatar) — null se não configurada */
  avatar_url: string | null;
}

export type ChatGroupKind = 'sector' | 'group' | 'direct' | 'support' | 'general';

/** Prévia da última mensagem — subcampo de ChatGroupSerializer.last_message */
export interface ChatGroupLastMessage {
  content: string;
  author_name: string;
  created_at: string;
  is_deleted: boolean;
}

/** ChatGroupSerializer — apps/chat/serializers/group_serializers/chat_group_serializer.py */
export interface ChatGroup {
  id: string;
  school: string;
  sector_id: string | null;
  name: string;
  /** 'sector' (auto-provisionado), 'group' (custom), 'direct' (DM 1:1),
   * 'support' (canal individual do usuário com o suporte Eleve, "Eleve
   * Martech") ou 'general' (1 por escola, auto-provisionado — equivalente
   * ao #general do Slack, todo mundo tem acesso implícito de leitura e
   * escrita) */
  kind: ChatGroupKind;
  is_archived: boolean;
  participant_count: number;
  /** Nome pronto para exibir — para DMs, já resolve para o nome do outro participante */
  display_name: string;
  /** Só preenchida para kind='group' com foto configurada — null caso contrário */
  avatar_url: string | null;
  last_message: ChatGroupLastMessage | null;
  /**
   * Só preenchido para kind='support' — o usuário da escola dono deste
   * canal individual. Cada usuário tem o seu próprio canal de suporte
   * (não é compartilhado pela escola); usado pela visão interna (staff)
   * para agrupar/rotular por usuário dentro de cada escola.
   */
  owner: ChatUserMini | null;
  /**
   * Só preenchido para kind='group' (grupo custom) — quem criou o grupo.
   * Usado para decidir quem pode remover OUTROS participantes: o próprio
   * criador, ou um gestor/coordenador/staff — remover a si mesmo (sair do
   * grupo) é sempre permitido, independente deste campo. null em grupos
   * criados antes deste campo existir, ou em qualquer outro kind.
   */
  created_by: ChatUserMini | null;
  created_at: string;
  updated_at: string;
}

/** ChatParticipantSerializer — apps/chat/serializers/participant_serializers/chat_participant_serializer.py */
export interface ChatParticipant {
  id: number;
  group: string;
  user: ChatUserMini;
  last_read_message_id: string | null;
  /** sequence da última mensagem lida — 0 se nunca leu nada */
  last_read_sequence: number;
  joined_at: string;
}

/** Categoria derivada do mime_type — StorageFile.category (backend) — decide
 * se o anexo tem preview inline (imagem/vídeo/áudio/pdf) ou só chip de
 * download ('file'). */
export type ChatAttachmentCategory = 'image' | 'video' | 'audio' | 'pdf' | 'file';

/** StorageFileMiniSerializer aninhado em ChatAttachmentSerializer */
export interface ChatAttachmentStorageFile {
  id: string;
  name: string;
  size: number;
  mime_type: string;
  category: ChatAttachmentCategory;
  /** URL presigned de leitura (disposition=inline), pronta pra <img>/<video>/
   * <audio src>. null se o arquivo foi removido do R2 ou a geração falhou —
   * nesse caso só resta o download tradicional (downloadAttachment). */
  url: string | null;
}

/** ChatAttachmentSerializer — apps/chat/serializers/attachment_serializers/chat_attachment_serializer.py */
export interface ChatAttachment {
  id: string;
  message: string;
  storage_file: ChatAttachmentStorageFile;
  uploaded_by: ChatUserMini;
  created_at: string;
}

export interface ChatReaction {
  emoji: string;
  count: number;
  /** ids numéricos de usuário (User.pk) que reagiram com este emoji */
  user_ids: number[];
}

/** ChatMessageSerializer — apps/chat/serializers/message_serializers/chat_message_serializer.py */
export interface ChatMessage {
  id: string;
  group: string;
  author: ChatUserMini | null;
  /** Chave de ordenação monotônica por grupo — usada para comparar com
   * ChatParticipant.last_read_sequence e resolver confirmação de leitura. */
  sequence: number;
  /** vira "[mensagem apagada]" quando is_deleted=true */
  content: string;
  /** ids numéricos de usuário mencionados */
  mentioned_users: number[];
  created_at: string;
  edited_at: string | null;
  is_deleted: boolean;
  reactions: ChatReaction[];
  attachments: ChatAttachment[];
  /** Presente quando esta mensagem compartilha um BoardCard — null caso contrário */
  card_share: ChatCardShare | null;
}

/** Resposta paginada por cursor (DRF CursorPagination) — core/pagination.py::MessageCursorPagination */
export interface ChatMessagePage {
  /** URL absoluta da próxima página (mensagens mais antigas) — ou null */
  next: string | null;
  /** URL absoluta da página anterior (mensagens mais novas) — ou null */
  previous: string | null;
  results: ChatMessage[];
}

export interface ChatUnreadCount {
  total: number;
  by_group: Record<string, number>;
}

export interface GetMessagesOptions {
  /** Filtro server-side — casa contra o texto da mensagem OU o título de
   * um card compartilhado (content__icontains OR card_share__title_snapshot__icontains). */
  search?: string;
  /**
   * URL absoluta vinda de `next`/`previous` de uma resposta anterior.
   * Quando informado, ignora `search` e busca essa página diretamente
   * (o cursor já carrega o estado de busca/ordenação original).
   */
  cursorUrl?: string;
}

export interface ToggleReactionResponse {
  added: boolean;
  message: ChatMessage;
}

/**
 * Preview de um BoardCard compartilhado no chat — visível a QUALQUER
 * participante do grupo, independente de setor. `can_view` é só um sinal
 * de UI (mostrar detalhe vs. "sem acesso"); o gate de segurança real é o
 * backend recusando `GET /chat/messages/{id}/card/` com 403.
 * ChatCardShareSerializer — apps/chat/serializers/card_share_serializers/.
 */
export interface ChatCardShare {
  id: string;
  card_id: string | null;
  title: string;
  board_name: string;
  sector_names: string[];
  /** false quando o card foi apagado/arquivado depois do compartilhamento */
  is_available: boolean;
  /** true se o usuário logado tem acesso ao setor deste card (mirror-aware) */
  can_view: boolean;
  shared_by: ChatUserMini | null;
  created_at: string;
}

// ── Service ───────────────────────────────────────────────────────────────────

export const chatService = {
  /**
   * Lista os grupos de chat acessíveis ao usuário autenticado.
   *
   * `schoolId` só tem efeito pra staff/superuser — por padrão eles veem
   * grupos de TODAS as escolas (necessário pro suporte via /interno/chat);
   * passar schoolId estreita pra uma escola só (usado por Chat.tsx quando
   * o usuário é staff, pra mostrar só os grupos da própria escola interna
   * "Eleve Interno" em vez de todas).
   */
  async listGroups(schoolId?: string): Promise<ChatGroup[]> {
    return api<ChatGroup[]>(`/chat/groups/${schoolId ? `?school_id=${schoolId}` : ''}`);
  },

  /** Detalhe de um grupo. */
  async getGroup(groupId: string): Promise<ChatGroup> {
    return api<ChatGroup>(`/chat/groups/${groupId}/`);
  },

  /** Participantes explícitos de um grupo. */
  async getGroupMembers(groupId: string): Promise<ChatParticipant[]> {
    return api<ChatParticipant[]>(`/chat/groups/${groupId}/members/`);
  },

  /** Cria um grupo custom (kind='group') com o usuário logado + `memberUserIds` como participantes. */
  async createGroup(name: string, memberUserIds: number[]): Promise<ChatGroup> {
    return api<ChatGroup>('/chat/groups/', {
      method: 'POST',
      body: { name, member_user_ids: memberUserIds },
    });
  },

  /** Find-or-create de uma conversa privada (DM) com `targetUserId` — nunca duplica. */
  async createOrOpenDirect(targetUserId: number): Promise<ChatGroup> {
    return api<ChatGroup>('/chat/groups/direct/', {
      method: 'POST',
      body: { target_user_id: targetUserId },
    });
  },

  /** Adiciona um participante a um grupo custom (kind='group'; recusado em 'sector'/'direct'). */
  async addParticipant(groupId: string, userId: number): Promise<ChatParticipant> {
    return api<ChatParticipant>(`/chat/groups/${groupId}/participants/`, {
      method: 'POST',
      body: { user_id: userId },
    });
  },

  /** Remove um participante de um grupo custom (kind='group'; recusado em 'sector'/'direct'). */
  async removeParticipant(groupId: string, userId: number): Promise<void> {
    return api<void>(`/chat/groups/${groupId}/participants/${userId}/`, { method: 'DELETE' });
  },

  /** Atualiza a foto de um grupo custom (kind='group'; recusado em 'sector'/'direct'). */
  async updateGroupAvatar(groupId: string, file: File): Promise<ChatGroup> {
    const formData = new FormData();
    formData.append('avatar', file);
    return api<ChatGroup>(`/chat/groups/${groupId}/avatar/`, { method: 'PATCH', body: formData });
  },

  /** Colegas da mesma escola, para o picker de "nova conversa" (exclui o próprio usuário). */
  async listColleagues(): Promise<ChatUserMini[]> {
    return api<ChatUserMini[]>('/chat/groups/colleagues/');
  },

  /** Avança o marcador de leitura do usuário logado até `messageId`. */
  async markRead(groupId: string, messageId: string): Promise<ChatParticipant> {
    return api<ChatParticipant>(`/chat/groups/${groupId}/mark-read/`, {
      method: 'POST',
      body: { message_id: messageId },
    });
  },

  /**
   * Histórico de mensagens de um grupo, paginado por cursor (50/página,
   * mais recentes primeiro). Passe `opts.cursorUrl` com o `next`/`previous`
   * de uma resposta anterior para paginar — `api()` aceita URL absoluta
   * diretamente (ver `path.startsWith('http')` em lib/api.ts).
   */
  async getMessages(groupId: string, opts: GetMessagesOptions = {}): Promise<ChatMessagePage> {
    if (opts.cursorUrl) {
      return api<ChatMessagePage>(opts.cursorUrl);
    }

    const params = new URLSearchParams();
    if (opts.search) params.set('search', opts.search);
    const qs = params.toString();
    return api<ChatMessagePage>(`/chat/groups/${groupId}/messages/${qs ? `?${qs}` : ''}`);
  },

  /**
   * Envia mensagem via REST (fallback quando o WebSocket não está conectado,
   * ou sempre que há anexo — anexar exige uma resposta síncrona com a
   * mensagem criada). `attachmentIds` são `StorageFile.id`s já enviados via
   * storageService.upload — a mensagem pode ir sem `content` quando há ao
   * menos um anexo (mesmo padrão de mídia solta do WhatsApp).
   */
  async sendMessage(
    groupId: string,
    content: string,
    mentionedUserIds?: number[],
    attachmentIds?: string[],
  ): Promise<ChatMessage> {
    return api<ChatMessage>(`/chat/groups/${groupId}/messages/`, {
      method: 'POST',
      body: {
        content,
        ...(mentionedUserIds?.length ? { mentioned_user_ids: mentionedUserIds } : {}),
        ...(attachmentIds?.length ? { attachment_ids: attachmentIds } : {}),
      },
    });
  },

  /**
   * Compartilha um BoardCard no grupo — cria uma mensagem com `card_share`
   * preenchido. `content` é uma legenda opcional; o backend recusa com 403
   * quando o usuário logado não tem acesso ao setor do card (ou de nenhum
   * board do mirror_group, se o card estiver espelhado).
   */
  async sendCardMessage(groupId: string, cardId: string, content?: string): Promise<ChatMessage> {
    return api<ChatMessage>(`/chat/groups/${groupId}/messages/`, {
      method: 'POST',
      body: { card_id: cardId, ...(content?.trim() ? { content: content.trim() } : {}) },
    });
  },

  /**
   * Detalhe COMPLETO (descrição, checklist, comentários, atividade) do card
   * compartilhado em `messageId`. Lança `ApiError` 403 quando o usuário não
   * tem acesso ao setor do card — o chamador deve tratar esse caso como
   * "sem acesso", não como falha genérica.
   */
  async getSharedCardDetail(messageId: string): Promise<unknown> {
    return api<unknown>(`/chat/messages/${messageId}/card/`);
  },

  /** Só o autor pode editar (403 caso contrário). */
  async editMessage(messageId: string, content: string): Promise<ChatMessage> {
    return api<ChatMessage>(`/chat/messages/${messageId}/`, {
      method: 'PATCH',
      body: { content },
    });
  },

  /** Soft delete — autor OU manager/coordinator (403 caso contrário). */
  async deleteMessage(messageId: string): Promise<void> {
    return api<void>(`/chat/messages/${messageId}/`, { method: 'DELETE' });
  },

  /** Alterna (adiciona/remove) a reação do usuário logado na mensagem. */
  async toggleReaction(messageId: string, emoji: string): Promise<ToggleReactionResponse> {
    return api<ToggleReactionResponse>(`/chat/messages/${messageId}/reactions/`, {
      method: 'POST',
      body: { emoji },
    });
  },

  /**
   * Vincula um arquivo já enviado (via storageService.upload) à mensagem.
   * `storageFileId` é o `StorageFile.id` retornado pelo upload.
   */
  async addAttachment(messageId: string, storageFileId: string): Promise<ChatAttachment> {
    return api<ChatAttachment>(`/chat/messages/${messageId}/attachments/`, {
      method: 'POST',
      body: { storage_file_id: storageFileId },
    });
  },

  /** Contagem de não lidas — total e por grupo. */
  async getUnreadCount(): Promise<ChatUnreadCount> {
    return api<ChatUnreadCount>('/chat/unread-count/');
  },

  /**
   * Download autenticado de um anexo (`GET /storage/{id}/download/`, streaming).
   * Usa fetch direto + blob (como nicoService.downloadReport) porque `api()`
   * sempre tenta `res.json()` — incompatível com resposta binária.
   */
  async downloadAttachment(storageFileId: string, fileName: string): Promise<void> {
    const token = getAccessToken();
    if (!token) throw new Error('Usuário não autenticado.');

    const res = await fetch(`${ENV.API_URL}/storage/${storageFileId}/download/`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!res.ok) {
      throw new Error(`Erro ${res.status} ao baixar anexo.`);
    }

    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },
};
